<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h1 class="text-uppercase">
          <span>{{ isEditPage ? $t('edit') : $t('create') }} {{ $t('programmatic_campaign') }}</span>
        </h1>
      </div>
      <div class="d-flex flex-row justify-content-center align-items-center gap-3 me-2">
        <div class="d-flex flex-row justify-content-center align-items-center gap-2" v-if="isSavingDraft">
          <Loader
            :size="'small'"
            :align="'start'"
          />
          {{ $t('saving_draft') }}
        </div>
        <Button
          class="btn btn-color10"
          v-b-modal.cancel-campaign-upsert-modal
        >
          {{ $t('cancel') }}
        </Button>
        <CancelCampaignUpsertModal
          :campaign="campaign"
          step="campaign"
        />
      </div>
    </div>
    <!-- CAMPAIGN FORM -->
    <div class="mt-3" v-if="!isEditPage || (isEditPage && campaign)">
      <div class="campaign-wrapper position-relative">
        <!-- INIT STEP -->
        <template v-if="step === 'init'">
          <div class="col-lg-8 mx-auto">
            <div class="card border-dashed border-color10">
              <div class="card-body">
                <p>
                  {{ $t('easy_campaign_pixel_roads') }}
                </p>
                <p>
                  {{ $t('important_thoughts_for_campaign') }}
                </p>
                <ul>
                  <li>{{ $t('campaign_goal') }};</li>
                  <li>{{ $t('campaign_duration') }}</li>
                  <li>{{ $t('available_budget') }}*<br/>
                    <span class="font-size-xSmall">* {{ $t('daily_budget_calculation') }}</span>
                  </li>
                  <li>{{ $t('creatives_in_formats_300x250_300x600_320x450_320x480_336x280_728x90_jpeg_png') }}*<br/>
                    <span class="font-size-xSmall">*{{ $t('required') }}</span>
                  </li>
                  <li>{{ $t('more_about_formats.0') }} <a href="https://pixelroads.com.br/blog/transforme-suas-campanhas-dicas-para-criar-banners-que-convertem/" target="_blank" class="link-color3">{{ $t('more_about_formats.1') }}</a>;</li>
                  <li>{{ $t('target_audience_and_locations') }}</li>
                  <li>{{ $t('suggest_pixel_pre_installed') }}</li>
                </ul>
                <div class="mt-4 text-center">
                  <Button
                    class="btn btn-color10"
                    @click="changeStep('campaign')"
                  >
                    {{ $t('start2') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </template> <!-- END INIT STEP -->
        <!-- HEADING -->
        <section class="my-4" v-if="step !== 'init'">
          <div class="text-center">
            <p>
              {{ stepDescription }}
              <span
                v-b-tooltip.hover
                :title="tooltips.pixels"
                v-if="step === 'preview'"
              >
                <i class="fa fa-info-circle font-size-regular"></i>
              </span>
            </p>
            <div class="mb-3 text-start">
              <a href="#" class="link-color2" @click.prevent="changeStep(previousStep)" v-if="previousStep"><i class="fa fa-angle-left me-2"></i>{{ $t('back') }}</a>
            </div>
          </div>
        </section> <!-- END HEADING -->
        <!-- CAMPAIGN STEP -->
        <template v-if="step === 'campaign'">
          <section class="mb-5">
            <div class="accordion accordion-flush" role="tablist">
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-1 variant="info">
                    {{ $t('campaign') }}
                  </button>
                </h2>
                <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <form @submit.prevent="" class="default-form">
                      <div class="d-flex flex-column flex-md-row">
                        <div class="col-12 col-md-6 pe-md-6">
                          <!-- ADVERTISER -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">
                              {{ $t('advertiser') }}*
                            </label>
                            <div v-if="advertisersLoader">
                              <Loader
                                :size="'small'"
                                :align="'start'"
                              />
                            </div>
                            <template v-else>
                              <div class="custom-select">
                                <select
                                  class="form-control"
                                  v-model.trim="form.advertiser_id"
                                  :disabled="campaign !== null && campaign.status !== 'DRAFT'"
                                >
                                  <option disabled value="">{{ $t('select') }}</option>
                                  <template v-if="advertisers.length > 0">
                                    <option v-for="(lAdvertiser, index) in advertisers" :key="index" :value="lAdvertiser.id">{{ lAdvertiser.name }}</option>
                                  </template>
                                </select>
                              </div>
                              <p
                                class="font-size-xSmall fw-bold mt-2 error-message-card"
                                v-if="selectedAdvertiser && selectedAdvertiser.status === 'ERROR'"
                              >
                                {{ $t('advertiser_creation_issue_access_page_try_again', {name:selectedAdvertiser.name}) }}
                              </p>
                            </template>
                          </div> <!-- END ADVERTISER -->
                          <!-- NAME -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('campaign_name') }}*</label>
                            <input type="text" class="form-control" :placeholder="$t('campaign_name')" v-model.trim="form.name" />
                          </div> <!-- END NAME -->
                        </div>
                        <div class="col-12 col-md-6 ps-md-6">
                          <!-- DATES -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('duration') }}*</label>
                            <div class="d-flex">
                              <!-- START DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.start_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="mx-2"
                                  :placeholder="$t('start')"
                                  :readonly="campaign && campaign.has_started"
                                />
                              </div> <!-- END START DATE -->
                              <!-- END DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.end_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="mx-2"
                                  :placeholder="$t('end')"
                                />
                              </div> <!-- END END DATE -->
                              <button @click="form.end_date = ''" v-if="form.end_date" class="btn btn-link text-color10 font-size-small px-2 py-0">
                                {{ $t('clear_end_date') }}
                              </button>
                            </div>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.start_date && !isCampaignStartDateValid"
                            >
                              {{ $t('start_date_not_before_today') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.end_date && !isCampaignEndDateValid"
                            >
                              {{ $t('end_date_validation') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.end_date && isCampaignEndDateBeforePackagesEndDate"
                            >
                              {{ $t('campaign_end_date_not_less_than_campaign_group_end_date') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.start_date && form.end_date && isCampaignEndDateBeforeStartDate"
                            >
                              {{ $t('end_date_not_before_start_date') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 alert-message-card"
                              v-if="form.start_date && form.end_date && isCampaignPeriodBelowRecommended"
                            >
                              {{ $t('campaign_minimum_duration_7_days') }}
                            </p>
                            <CampaignDateUpdateModal
                              :packages="campaign.packages"
                              :selected="form.update_package_ids"
                              @submit="handleCampaignDateUpdateConfirmation"
                              v-if="isEditPage && form.end_date"
                            />
                          </div> <!-- END DATES -->
                          <!-- AMOUNT -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('budget') }}*</label>
                            <Money
                              class="form-control mb-3"
                              v-model.trim="form.amount"
                              v-bind="money"
                            />
                            <template v-if="selectedAdvertiser">
                              <div class="d-flex align-items-center flex-wrap">
                                <div class="col-12 col-lg-7">
                                  <p
                                    class="font-size-xSmall fw-bold mt-2 error-message-card"
                                    v-if="hasCampaignExceededAvailableAdvertiserAmount"
                                  >
                                    {{ $t('campaign_budget_cannot_exceed') }}
                                  </p>
                                  <p class="font-size-xSmall fw-bold">
                                    {{ $t('available_balance') }}: {{ selectedAdvertiser.available_amount / 100 | formatMoney(selectedAdvertiser.currency) }}
                                  </p>
                                </div>
                              </div>
                              <div class="text-lg-end d-flex align-items-center justify-content-center justify-content-lg-end">
                                <Button
                                  class="btn btn-color10 btn-xs me-2"
                                  @click="addAllAvailableAmountToCampaign"
                                >
                                  {{ $t('use_all_balance') }}
                                </Button>
                              </div>
                            </template>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="!checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                            >
                              {{ $t('recommended_minimum_daily_budget', {value:minimumDailyBudget, date:this.$options.filters.formatDate(suggestedEndDate)}) }}
                            </p>
                          </div><!-- END AMOUNT -->
                        </div>
                      </div>
                      <!-- ACTION -->
                      <div class="mt-4 text-center">
                        <Button
                          class="btn btn-color10"
                          @click="confirmPackagesAndProceed"
                          :disabled="!isCampaignStepComplete || isSavingDraft || !checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                        >
                          {{ $t('next') }}
                        </Button>
                      </div> <!-- END ACTION -->
                    </form>
                  </div>
                </b-collapse>
              </div>

              <div class="accordion-item border-bottom border-dashed" v-if="!isEditPage">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-2 variant="info" :disabled="!isCampaignStepComplete">
                    {{ $t('campaign_package') }}<span v-b-tooltip.hover :title="tooltips.packages"><i class="fa fa-info-circle font-size-xRegular ps-2 text-color10"></i></span>
                  </button>
                </h2>
                <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <CampaignPackageForm
                      v-if="isCampaignStepComplete"
                      :base-campaign="form"
                      :currency="selectedAdvertiser.currency"
                      @form-package="handleFormPackage"
                    />
                  </div>
                </b-collapse>
              </div>

              <div class="accordion-item border-bottom border-dashed" v-if="!isEditPage">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-3 variant="info" :disabled="!isCampaignStepComplete || !form.package">
                    {{ $t('campaign_line') }}<span v-b-tooltip.hover :title="tooltips.lines"> <i class="fa fa-info-circle font-size-xRegular ps-2 text-color10"></i></span>
                  </button>
                </h2>
                <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <template v-if="isCampaignStepComplete">
                      <!-- Strategies share the budget of the group: the platform allocates it automatically -->
                      <p class="font-size-xSmall fw-bold mt-2 alert-message-card">
                        {{ $t('strategies_share_budget_notice', { amount: packageAmountLabel }) }}
                      </p>
                      <p class="font-size-xSmall fw-bold mt-2 alert-message-card" v-if="strategies.length > recommendedMaxStrategies">
                        {{ $t('strategies_recommended_limit_warning', { max: recommendedMaxStrategies }) }}
                      </p>
                      <ul class="nav strategy-tabs mb-4">
                        <li class="nav-item" v-for="(strategy, index) in strategies" :key="strategy.uid">
                          <a
                            href="#"
                            class="strategy-tab"
                            :class="{ active: index === activeStrategy }"
                            @click.prevent="selectStrategy(index)"
                          >
                            <span class="strategy-tab__index">{{ index + 1 }}</span>
                            <span class="strategy-tab__name">{{ strategy.name || `${$t('strategy')} ${index + 1}` }}</span>
                            <i v-if="strategy.complete" class="fa fa-check-circle text-success"></i>
                          </a>
                        </li>
                        <li class="nav-item">
                          <a
                            href="#"
                            class="strategy-tab strategy-tab--add"
                            :aria-label="$t('add_new_strategy')"
                            @click.prevent="addStrategy"
                          >
                            <i class="fa fa-plus"></i>
                            <span class="d-none d-md-inline">{{ $t('add_new_strategy') }}</span>
                          </a>
                        </li>
                      </ul>
                      <div class="d-flex justify-content-end mb-3">
                        <a href="#" class="me-3" @click.prevent="duplicateStrategy"><i class="fa fa-copy me-1"></i>{{ $t('duplicate_strategy') }}</a>
                        <a href="#" class="text-danger" v-if="strategies.length > 1" @click.prevent="removeStrategy"><i class="fa fa-trash me-1"></i>{{ $t('remove_strategy') }}</a>
                      </div>
                      <!-- Only the active strategy is mounted: its modals have static ids and can't be duplicated -->
                      <CampaignLineForm
                        ref="lineForm"
                        :key="strategies[activeStrategy].uid"
                        :base-campaign="form"
                        :advertiser="selectedAdvertiser"
                        :multi="true"
                        :strategy-uid="strategies[activeStrategy].uid"
                        :initial-state="strategies[activeStrategy].state"
                        @status="handleStrategyStatus"
                        @snapshot="saveStrategySnapshot"
                      />
                      <div class="text-end mt-4">
                        <Button class="btn-color10" :disabled="!areAllStrategiesComplete" @click="goToPixelsStep">
                          {{ $t('next') }}
                        </Button>
                      </div>
                    </template>
                  </div>
                </b-collapse>
              </div>

              <div class="accordion-item">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-4 variant="info" :disabled="!isCampaignStepComplete">
                    {{ $t('pixels') }}
                  </button>
                </h2>
                <b-collapse id="accordion-4" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <div class="mb-5">
                      <p
                        class="text-center font-size-xSmall fw-bolder mt-2 alert-message-card"
                      >
                        {{ $t('select_pixel_message') }}
                      </p>
                      <p
                        class="text-center font-size-xSmall fw-bolder mt-2"
                      >
                        {{ $t('learn_more_about_pixel') }}, <a href="https://pixelroads.com.br/blog/pixel-academy/1-8-1-explicacao-de-pixels/" target="_blank">{{ $t('click_here') }}</a>
                      </p>
                      <!-- ACTION -->
                      <div class="text-center" v-if="selectedAdvertiser">
                        <div>
                          <router-link
                            :to="{ name: 'pixels.index' }"
                            class="btn btn-color10 mb-4 me-2 btn-sm"
                            target="_blank"
                          >
                            {{ $t('add_new_pixel') }}
                          </router-link>
                          <div class="border border-dashed border-color10 p-5">
                            <SelectPixelRule
                              :advertiserId="form.advertiser_id"
                              @pixel-rule-selected="handleSelectedPixelRule"
                            />
                          </div>
                        </div>
                      </div> <!-- END ACTION -->
                      <template v-if="form.pixels.length > 0">
                        <div class="mt-4 text-center border border-dashed border-color10 p-5">
                          <h2 class="text-uppercase font-size-regular fw-500">
                            <span>{{ $t('selected_pixels') }}</span>
                          </h2>
                          <table class="table table-striped align-middle table-row-dashed fs-6 gy-2 dataTable no-footer">
                            <thead>
                              <tr>
                                <th>{{ $t('pixel_name') }}</th>
                                <th>{{ $t('rule_name') }}</th>
                                <th>{{ $t('objective') }}</th>
                                <th>{{ $t('event_name') }}</th>
                                <th width="100">{{ $t('actions') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr class="text-nowrap" v-for="(rule, index) in form.pixels" :key="index">
                                <td>{{ rule.pixel.name }}</td>
                                <td>{{ rule.name }}</td>
                                <td>{{ rule.objective | formatObjective }}</td>
                                <td>{{ rule.event_name }}</td>
                                <td>
                                  <Button
                                    class="btn-danger btn-xs font-size-small btn-circle p-0 mb-2"
                                    @click.prevent="openDestroyPixelRuleModal(rule)"
                                    v-b-tooltip.hover :title="$t('remove_rule')"
                                  >
                                    <i class="fa fa-trash px-3"></i>
                                  </Button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </template>
                      <DestroyPixelRuleModal
                        :rule="selectedPixelRule"
                        @pixel-rule-destroyed="destroyPixelRule"
                      />
                    </div>
                  </div>
                </b-collapse>
              </div>
            </div>
            <div class="mt-5 text-center">
              <!-- MESSAGES -->
              <div
                class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2"
                v-if="loader"
              >
                <p
                  class="mb-0"
                >
                  {{ $t('this_action_may_take_a_few_minutes') }}
                </p>
              </div> <!-- END MESSAGES -->
              <Button
                class="btn btn-color10"
                v-if="!isEditPage"
                @click="clickSubmit"
                :disabled="!isCampaignStepComplete || isSavingDraft || !form.package || !areAllStrategiesComplete"
                :loading="loader || isSavingDraft"
              >
                {{ $t('create_campaign') }}
              </Button>
              <Button
                class="btn btn-color10"
                v-if="isEditPage"
                @click="clickSubmit"
                :disabled="!isCampaignStepComplete || isSavingDraft"
                :loading="loader || isSavingDraft"
              >
                {{ $t('save_campaign') }}
              </Button>
            </div>
          </section>
        </template> <!-- END CAMPAIGN STEP -->
      </div>
    </div><!-- END CAMPAIGN FORM -->
  </div>
</template>

<script>

import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import { getMoneyFormat } from '@/utils'
import variables from '@/mixins/variables'

import Button from '@/components/common/Button/Button'
import CampaignDateUpdateModal from '@/components/campaigns/Programmatic/CampaignDateUpdateModal'
import CampaignLineForm from '@/components/campaigns/Programmatic/CampaignLineForm'
import CampaignPackageForm from '@/components/campaigns/Programmatic/CampaignPackageForm'
import CancelCampaignUpsertModal from '@/components/campaigns/Modals/CancelCampaignUpsertModal'
import DateTimeInput from '@/components/common/Input/DateTimeInput'
import DestroyPixelRuleModal from '@/components/campaigns/UpsertForm/DestroyPixelRuleModal'
import Loader from '@/components/common/Loader/Loader'
import { Money } from 'v-money'
import SelectPixelRule from '@/components/pixels/SelectPixelRule'

import { mapState } from 'vuex'
import moment from 'moment'

export default {
  mixins: [variables],
  props: ['baseCampaign'],
  components: {
    Button,
    CampaignDateUpdateModal,
    CampaignLineForm,
    CampaignPackageForm,
    CancelCampaignUpsertModal,
    DateTimeInput,
    DestroyPixelRuleModal,
    Loader,
    Money,
    SelectPixelRule
  },
  data () {
    return {
      autoSaveInterval: null,
      advertisers: [],
      advertisersLoader: false,
      campaign: null,
      form: {
        advertiser_id: '',
        name: '',
        start_date: '',
        end_date: '',
        amount: '',
        pixels: [],
        type: 'PROGRAMMATIC',
        update_package_ids: [],
        package: null,
        line: null,
        lines: []
      },
      activeStrategy: 0,
      // Recommended (not enforced) limit of strategies per campaign
      recommendedMaxStrategies: 5,
      // One entry per strategy: { uid, name, state (to restore the form), prepared (payload), complete }
      strategies: [{ uid: 1, name: '', state: null, prepared: null, complete: false }],
      strategyUidCounter: 1,
      isSavingDraft: false,
      loader: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },
      minimumDailyBudget: 10,
      original_end_date: '',
      original_start_date: '',
      selectedPixelRule: '',
      step: 'init'
    }
  },
  created () {
    if (this.isEditPage) {
      this.step = 'campaign'
      this.getCampaign()
    }
    if (this.user) this.getPageContent()
    // this.setAutoSaveInterval()
  },
  beforeDestroy () {
    this.clearAutoSaveInterval()
  },
  watch: {
    user (newUser) {
      this.getPageContent()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    /**
     * check if daily budget is bigger than minimum daily budget
     */
    checkIfDailyBudgetIsBiggerThanMinimumDailyBudget () {
      if (this.form.end_date && this.suggestedEndDate && this.form.amount > 0) {
        const startDate = moment(this.form.start_date)
        const endDate = moment(this.form.end_date)
        const campaignDays = endDate.diff(startDate, 'days') + 1
        const totalBudget = campaignDays * this.minimumDailyBudget

        return this.form.amount >= totalBudget
      }
      return true
    },
    /**
     * Check if campaign amount has exceeded what advertiser has available
     */
    hasCampaignExceededAvailableAdvertiserAmount () {
      if (!this.selectedAdvertiser) return true
      // If is new campaign, the only thing that matters is the full available amount
      const amount = this.form.amount * 100
      const available = this.selectedAdvertiser.available_amount
      if (!this.campaign) {
        return amount > available
      }

      // If is campaign that already exists, calculate the difference between new amount and old amount and compare that value to available amount
      const difference = amount - this.campaign.amount
      return difference > available
    },
    /**
     * Is campaign end date valid
     */
    isCampaignEndDateValid () {
      if (this.form.end_date) {
        const date = moment(this.form.end_date)
        // Check if is update and if value hasnt changed from original
        if (this.campaign && !this.isCampaignDraft) {
          const then = moment(this.campaign.end_date)
          if (date.isSame(then)) return true
        }
        const tomorrow = moment().startOf('day').add(1, 'day')
        return date.isSameOrAfter(tomorrow)
      }
      return true
    },
    /**
     * Is campaign status draft
     */
    isCampaignDraft () {
      return this.campaign.status === 'DRAFT'
    },
    /**
     * Checks if campaign end date is before start date
     */
    isCampaignEndDateBeforeStartDate () {
      if (this.form.end_date && !this.form.start_date) return true
      const startDate = moment(this.form.start_date)
      const endDate = moment(this.form.end_date)

      return endDate.isSameOrBefore(startDate)
    },
    /**
     * Check if campaign end date is before campaign packages end date
     */
    isCampaignEndDateBeforePackagesEndDate () {
      if (!this.campaign) return false
      let flag = false
      const endDate = moment(this.form.end_date)

      this.campaign.packages.forEach(campaignPackage => {
        const packageEndDate = moment(campaignPackage.end_date)
        if (endDate.isBefore(packageEndDate)) flag = true
      })

      return flag
    },
    /**
     * Check if campaign period is below recommended
     */
    isCampaignPeriodBelowRecommended () {
      if (!this.form.start_date || !this.form.end_date) return true
      const start = moment(this.form.start_date)
      const end = moment(this.form.end_date)
      return end.diff(start, 'days') < 7
    },
    /**
     * Is campaign start date valid
     */
    isCampaignStartDateValid () {
      if (!this.form.start_date) return false
      const date = moment(this.form.start_date)
      // Check if is update of started campaign and if value hasnt changed from original
      if (this.campaign && !this.isCampaignDraft) {
        const then = moment(this.campaign.start_date)
        if (date.isSame(then)) return true
      }
      const today = moment().endOf('day')
      return date.isAfter(today)
    },
    /**
     * Check if every strategy is complete
     */
    areAllStrategiesComplete () {
      return this.strategies.length > 0 && this.strategies.every(item => item.complete)
    },
    /**
     * Budget of the group (shared by all strategies), formatted with the advertiser currency
     */
    packageAmountLabel () {
      if (!this.form.package) return ''
      const amount = parseInt(this.form.package.amount) / 100
      try {
        return amount.toLocaleString('pt-BR', { style: 'currency', currency: this.selectedAdvertiser?.currency || 'BRL' })
      } catch (error) {
        return String(amount)
      }
    },
    /**
     * Check if campaign is complete
     */
    isCampaignStepComplete () {
      if (
        !this.form.advertiser_id ||
        !this.form.name ||
        !this.form.start_date ||
        parseFloat(this.form.amount) <= 0 ||
        this.hasCampaignExceededAvailableAdvertiserAmount ||
        !this.isCampaignStartDateValid ||
        !this.isCampaignEndDateValid ||
        this.isCampaignEndDateBeforeStartDate ||
        !this.selectedAdvertiser ||
        this.selectedAdvertiser.status === 'ERROR' ||
        this.isCampaignEndDateBeforePackagesEndDate
      ) {
        return false
      }

      return true
    },
    /**
     * Check if is edit page
     */
    isEditPage () {
      return this.$route.name === 'campaigns.edit'
    },
    /**
     * Get previous step
     */
    previousStep () {
      switch (this.step) {
        case 'campaign':
          return null
        case 'preview':
          return 'campaign'
        default:
          return null
      }
    },
    /**
     * Router name
     */
    routeName () {
      return this.$route.name
    },
    /**
     * Selected advertiser
     */
    selectedAdvertiser () {
      if (!this.form.advertiser_id) return null

      return this.advertisers.find(item => {
        this.money = getMoneyFormat(item.currency)

        return item.id === this.form.advertiser_id
      })
    },
    /**
     * Step description
     */
    stepDescription () {
      switch (this.step) {
        case 'campaign':
          return this.$t('create_basic_campaign_settings')
        case 'preview':
          return this.$t('configure_tracking_pixels')
        default:
          return ''
      }
    },
    /**
     * calculate campaign end date, with minimum Daily Budget
     */
    suggestedEndDate () {
      if (!this.form.start_date || !this.form.end_date) return null

      const startDate = moment(this.form.start_date)

      const suggestedEndDate = startDate.clone().add(Math.floor(this.form.amount / this.minimumDailyBudget), 'days').subtract(1, 'day')
      return suggestedEndDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    }
  },
  methods: {
    /**
     * Add all selected advertiser available amount to campaign
     */
    addAllAvailableAmountToCampaign () {
      if (!this.selectedAdvertiser) return
      // If is creation, use full amount
      if (!this.isEditPage) {
        this.form.amount = (this.selectedAdvertiser.available_amount / 100)
      } else {
        // If is edit page, add the amount to what is in the form
        this.form.amount = this.form.amount + (this.selectedAdvertiser.available_amount / 100)
      }
    },
    /**
     * Change step of form
     */
    changeStep (step) {
      this.step = step
    },
    /**
     * Clear auto save interval
     */
    clearAutoSaveInterval () {
      // this.isAutoSaveEnabled = false
      clearInterval(this.autoSaveInterval)
    },
    /**
     * On click submit button
     */
    clickSubmit () {
      this.submit(false)
    },
    /**
     * Check if campaign has more than one package and if is date update to show modal
    */
    confirmPackagesAndProceed () {
      if (!this.baseCampaign) this.$root.$emit('bv::toggle::collapse', 'accordion-2')
      if ((this.form.start_date !== this.original_start_date) ||
      (this.form.end_date && (this.form.end_date !== this.original_end_date))) {
        this.$bvModal.show('upsert-campaign-date-modal')
      } else {
        this.$root.$emit('bv::toggle::collapse', 'accordion-4')
      }
    },
    /**
     * Remove pixel rule from rules array
     */
    destroyPixelRule (rule) {
      const index = this.form.pixels.findIndex(item => {
        return item.id === rule.id
      })
      this.form.pixels.splice(index, 1)
    },
    /**
     * Get advertisers
     */
    async getAdvertisers () {
      this.advertisersLoader = true
      try {
        const advertisers = await AdvertisersService.getAdvertisers({
          companyId: this.user.company.id,
          status: 'ACTIVE',
          itemsPerPage: 100,
          viewType: 'listing_for_campaign_creation'
        })
        this.advertisers = advertisers.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.advertisersLoader = false
    },
    /**
     * Get advertiser
     */
    async getCampaign () {
      if (!this.baseCampaign) return
      this.campaign = this.baseCampaign
      this.setCampaign(this.baseCampaign)
    },
    /**
     * Get necessary page content
     */
    getPageContent () {
      this.getAdvertisers()
    },
    /**
     * Handle campaign date update confirmation
    */
    handleCampaignDateUpdateConfirmation (packages) {
      this.form.update_package_ids = packages
      this.$root.$emit('bv::toggle::collapse', 'accordion-4')
    },
    handleFormPackage (campaignPackage) {
      this.form.package = campaignPackage
      this.$root.$emit('bv::toggle::collapse', 'accordion-3')
    },
    /**
     * Strategies (campaign lines)
     */
    addStrategy () {
      this.captureActiveStrategy()
      this.strategies.push({ uid: ++this.strategyUidCounter, name: '', state: null, prepared: null, complete: false })
      this.activeStrategy = this.strategies.length - 1
    },
    duplicateStrategy () {
      this.captureActiveStrategy()
      const source = this.strategies[this.activeStrategy]
      const state = source.state ? JSON.parse(JSON.stringify(source.state)) : null
      if (state && state.form.name) state.form.name = `${state.form.name} (${this.$t('copy')})`
      this.strategies.push({
        uid: ++this.strategyUidCounter,
        name: state ? state.form.name : '',
        state,
        prepared: null,
        complete: false
      })
      this.activeStrategy = this.strategies.length - 1
    },
    removeStrategy () {
      if (this.strategies.length <= 1) return
      // The strategy is discarded, so its form must not hand a snapshot back
      this.strategies.splice(this.activeStrategy, 1)
      this.activeStrategy = Math.max(0, this.activeStrategy - 1)
    },
    selectStrategy (index) {
      if (index === this.activeStrategy) return
      this.captureActiveStrategy()
      this.activeStrategy = index
    },
    /**
     * Save the data of the mounted strategy form
     */
    captureActiveStrategy () {
      const form = this.$refs.lineForm
      if (!form) return
      this.saveStrategySnapshot(form.collect())
    },
    // The snapshot carries the uid of the strategy that emitted it (the form may already be unmounted)
    saveStrategySnapshot (snapshot) {
      const strategy = this.strategies.find(item => item.uid === snapshot.uid)
      if (!strategy) return
      strategy.state = snapshot.state
      strategy.prepared = snapshot.prepared
      strategy.complete = snapshot.complete
      strategy.name = snapshot.state.form.name
    },
    handleStrategyStatus ({ uid, complete }) {
      const strategy = this.strategies.find(item => item.uid === uid)
      if (strategy) strategy.complete = complete
    },
    goToPixelsStep () {
      this.captureActiveStrategy()
      this.$root.$emit('bv::toggle::collapse', 'accordion-4')
    },
    /**
     * Handle selected pixel rule
     */
    handleSelectedPixelRule (rule) {
      const index = this.form.pixels.findIndex(item => {
        return item.id === rule.id
      })
      if (index >= 0) return
      this.form.pixels.push(rule)
    },
    /**
     * Open modal to destroy pixel rule
     */
    openDestroyPixelRuleModal (rule) {
      this.selectedPixelRule = rule
      this.$bvModal.show('destroy-pixel-rule-modal')
    },
    /**
     * Save draft
     */
    saveDraft () {
      if (!this.selectedAdvertiser) return
      if (this.campaign && this.campaign.status !== 'DRAFT') return
      this.submit(true)
    },
    /**
     * Create auto save interval
     */
    setAutoSaveInterval () {
      this.isAutoSaveEnabled = true
      this.autoSaveInterval = setInterval(() => {
        this.saveDraft()
      }, 30000)
    },
    /**
     * Set campaign
     */
    setCampaign (campaign) {
      this.form.advertiser_id = campaign.advertiser.id
      this.form.name = campaign.name

      this.form.start_date = campaign.start_date
      this.original_start_date = new Date(campaign.start_date).toISOString().split('T')[0] + 'T00:00:00.000-03:00'
      this.form.end_date = campaign.end_date
      this.original_end_date = campaign.end_date ? new Date(campaign.end_date).toISOString().split('T')[0] + 'T00:00:00.000-03:00' : null
      this.form.amount = campaign.amount / 100
      this.form.pixels = campaign.pixels.map(item => {
        return {
          id: item.id,
          name: item.name,
          objective: item.objective,
          event_name: item.event_name,
          pixel: item.pixel
        }
      })
    },
    /**
     * Submit
     */
    async submit (isDraft = false) {
      this.clearAutoSaveInterval()
      if (isDraft) this.isSavingDraft = true
      else this.loader = true
      try {
        // Create copy of form that can be changed (object assign wont work for multi-layered objects)
        const form = JSON.parse(JSON.stringify(this.form))

        // Parse amount
        form.amount = parseInt(form.amount * 100)
        form.pixels = this.form.pixels.map(item => {
          return item.id
        })
        form.update_package_ids = this.form.update_package_ids.map(item => {
          return item.code
        })
        // Strategies: "lines" has all of them, "line" (first one) is kept for backwards compatibility
        if (!this.campaign) {
          this.captureActiveStrategy()
          const lines = this.strategies.map(item => item.prepared).filter(Boolean)
          form.lines = lines
          form.line = lines.length > 0 ? lines[0] : null
        }
        let campaign = null
        form.is_draft = isDraft
        if (!this.campaign) {
          campaign = await CampaignsService.createCampaign(form)
        } else {
          campaign = await CampaignsService.updateCampaign(this.campaign.id, form)
        }
        if (!isDraft) {
          this.$router.push({ name: 'campaigns.show', params: { id: campaign.id } })
        } else {
          this.campaign = campaign
        }
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      if (isDraft) this.isSavingDraft = false
      else this.loader = false
      // Reenable autosave
      this.setAutoSaveInterval()
    }
  }
}
</script>

<style lang="scss">
  // Tabs of the strategies (campaign lines)
  .strategy-tabs {
    flex-wrap: nowrap;
    gap: 0.5rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid rgba(0, 0, 0, 0.08);

    .strategy-tab {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.85rem 1.4rem;
      font-size: 1rem;
      font-weight: 600;
      white-space: nowrap;
      color: inherit;
      text-decoration: none;
      background: rgba(0, 0, 0, 0.03);
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 0.6rem;

      &.active {
        color: #fff;
        background: $color10;
        border-color: $color10;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

        .strategy-tab__index {
          color: $color10;
          background: #fff;
        }
      }

      &--add {
        font-weight: 700;
        background: transparent;
        border: 1px dashed $color10;
        color: $color10;
      }
    }

    .strategy-tab__index {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.7rem;
      height: 1.7rem;
      font-size: 0.85rem;
      font-weight: 700;
      color: #fff;
      background: $color10;
      border-radius: 50%;
    }

    .strategy-tab__name {
      max-width: 14rem;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
</style>
